/* eslint-disable react-hooks/exhaustive-deps */
import { Avatar, Box, Button, CircularProgress, Typography } from '@mui/material';
import { Page, PassFail, Searchbar, Table } from '../../design';
import { useRequester } from '../../hooks';
import { useEffect, useState, useContext } from 'react';
import { ChatResource, StudentData } from '../../resources';
import { AuthContext } from '../../contexts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate, formatTime } from '../../utils';
import { testTypeLabelsFull } from './ChatPage';

interface Row {
  id: string;
  type: string;
  duration: number;
  createdAt: string;
  student: StudentData;
  meta: any;
  score: string;
  passfail: string | undefined;
}

type Query = {
  offset: number;
  limit: number;
};

function NameCell(props: Row) {
  return (
    <Box className="flex items-center gap-4">
      <Avatar src={props.student.avatar} />
      <div>
        <Typography variant="h6" color="text.primary">
          {props.student.firstName} {props.student.lastName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {props.student.email}
        </Typography>
      </div>
    </Box>
  );
}

function TypeCell(props: Row) {
  return (
    <Box className="flex flex-col items-center gap-1">
      <Typography variant="h6" color="text.primary">
        Eiken Grade2 Interview
      </Typography>
      <Typography variant="h6" color="text.secondary">
        {testTypeLabelsFull[props.meta?.eikenTestType as keyof typeof testTypeLabelsFull]}
      </Typography>
    </Box>
  );
}

function PassFailCell(props: Row) {
  return (
    <Box className="flex justify-center">
      <PassFail type={props.passfail} />
    </Box>
  );
}

function ScoreCell(props: Row) {
  return (
    <Typography variant="h3" color="text.primary">
      {props.score}
    </Typography>
  );
}

const columns = [
  {
    id: 'student',
    label: 'Name',
    Component: NameCell,
  },
  {
    id: 'type',
    label: 'Question Type',
    Component: TypeCell,
  },
  {
    id: 'score',
    label: 'Score',
    align: 'center',
    Component: ScoreCell,
  },
  {
    id: 'passfail',
    label: 'Pass/Fail',
    align: 'center',
    Component: PassFailCell,
  },
  {
    id: 'createdAt',
    label: 'Practice At',
    align: 'center',
  },
  {
    id: 'duration',
    label: 'Duration',
    align: 'center',
    format: (value: number) => `${formatTime(value)}`,
  },
];

const toRow = (ob: ChatResource): Row => {
  const score = ChatResource.getScoreForType(ob.data, ob.data.meta?.eikenTestType);
  let passfail;
  if (score.score === undefined) {
    passfail = undefined;
  } else {
    passfail = Number(score.score) >= score.passScore ? 'pass' : 'fail';
  }

  return {
    id: ob.data.id,
    type: ob.data.type,
    meta: ob.data.meta,
    duration: ob.data.duration,
    createdAt: formatDate(ob.data.createdAt),
    student: ob.data.student,
    score: `${score.score ?? '-'}/${score.maxScore}`,
    passfail,
  };
};

export default function ChatsPage() {
  const { user } = useContext(AuthContext);
  const [params] = useSearchParams();
  const userId = params.get('userId');
  const navigate = useNavigate();

  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [query, setQuery] = useState<Query>({
    offset: 0,
    limit: 50,
  });
  const chatsApi = useRequester<ChatResource[]>({
    onSubmit: (q) => ChatResource.fetchMany(q),
    onSuccess: (data) => setHasReachedBottom(data.length < query.limit),
    onError: console.log,
  });

  useEffect(() => {
    chatsApi.submit({
      ...query,
      userId,
      type: 'eiken2',
      includeOthers: true,
    });
  }, [query.offset, userId]);

  const rows = ChatResource.list().map(toRow);

  return (
    <Page>
      <Page.Header>
        <Box className="flex flex-col h-full">
          <Typography variant="body1" color="text.secondary" sx={{ mt: 8 }}>
            👋 Hey, {user?.data.firstName}!
          </Typography>
          <Typography variant="h1">Practice History</Typography>
          <Box className="flex-grow"></Box>
        </Box>
      </Page.Header>
      <Page.Content>
        <Box className="flex justify-between mt-8 mb-8">
          <Box className="flex gap-4">
            <Searchbar disabled placeholder="Search for a practice" onChange={(qs) => { }} />

            {chatsApi.loading && (
              <Box className="flex items-center">
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        </Box>

        <Table
          rows={rows}
          // @ts-ignore
          columns={columns}
          onRowClick={(row) => navigate(`/chats/${row.id}`)}
        />
        {!hasReachedBottom && rows.length > 0 && (
          <Button
            disabled={chatsApi.loading}
            sx={{ mt: 2 }}
            fullWidth
            onClick={() => setQuery({ ...query, offset: query.offset + 50 })}
          >
            Load More
          </Button>
        )}
      </Page.Content>
    </Page>
  );
}
